import Dexie from 'dexie';
import moment from 'moment';
import {
    getLastUserCollectionDates,
    getPrevUserCollectionDate
} from '../../features/Personalization/service/indexDBService';
import {EntityType} from '../../constants/EntityTypes';
import {getUsername} from '../../_shared/GWTBridge/invoke/genPage';
import {configuredDate} from '../../features/Personalization/utils/personalization';
import {getNeverShowAgainServer, checkClearStorage} from '../../features/Personalization/actions';
import {configuration as baseConfiguration} from '../../features/Personalization/config';

interface DataItem {
    date: string;
    count: number;
}
interface Entity {
    name: string;
    collectionDate?: string;
    type: string;
    title: string;
    exec: string;
    user: string;
    data?: DataItem[];
}

interface CollectionDate {
    date: string;
    id?: number;
    user: string;
    neverShowAgain: boolean;
}

interface BlackList {
    user: string;
    name: string;
    id?: number;
}
class EntityDatabase extends Dexie {
    public entitys: Dexie.Table<Entity, string>;
    public collectionDates: Dexie.Table<CollectionDate, number>;
    public blackList: Dexie.Table<BlackList, number>;

    constructor() {
        super('EntityDatabase');

        this.version(1).stores({
            entitys:
                '[name+collectionDate+type+user], [collectionDate+user], collectionDate, name,type',
            collectionDates: '++id, [date+user], user, date',
            blackList: '[name+user+type], user, name'
        });
    }
}

const IGNORED_ENTITIES = ['ANALYTICS'];

export const db = new EntityDatabase();

export const startNewCollection = async () => {
    const today = configuredDate().format('DD/MM/YYYY');
    await db.transaction('rw', db.collectionDates, async () => {
        db.collectionDates
            .add({date: today, user: getUsername(), neverShowAgain: false})
            .catch(e => console.log('error adding new collection', e));
    });
    return today;
};

export const addEntryIndexedDB = ({
    title,
    name,
    exec,
    type
}: {
    title: string;
    name: string;
    exec: string;
    type: string;
}) => {
    if (IGNORED_ENTITIES.includes(name)) return;
    let entityType: EntityType;
    switch (type.toLowerCase()) {
        case 'e':
            entityType = EntityType.PROCEDURE;
            break;
        case 'p':
            entityType = EntityType.PROCEDURE;
            break;
        case 'w':
            entityType = EntityType.WIZARD;
            break;
        case 'r':
            entityType = EntityType.REPORT;
            break;
        case 'f':
            entityType = EntityType.FORM;
            break;
        case 'g':
            entityType = EntityType.GANTT;
            break;
        case 'h':
            entityType = EntityType.DOCUMENT;
            break;
        case 'd':
            entityType = EntityType.DASHBOARD;
            break;
        case 'o':
            entityType = EntityType.BIREPORT;
            break;
    }

    addNewEntry({title, name, type: entityType, exec: exec, user: getUsername()});
};

export const addNewEntry = async (entity: Entity) => {
    db.transaction('rw', db.entitys, db.collectionDates, async () => {
        const lastCollection = await getLastUserCollectionDates(db, getUsername());
        const collectionDate = lastCollection?.date || (await startNewCollection());
        const today = configuredDate().format('DD/MM/YYYY');

        if (!lastCollection?.neverShowAgain) {
            const entityToUpdate = await db.entitys.get({
                name: entity.name,
                collectionDate,
                type: entity.type,
                user: getUsername()
            });

            if (entityToUpdate) {
                db.entitys
                    .where('[name+collectionDate+type+user]')
                    .equals([entity.name, collectionDate, entity.type, getUsername()])
                    .modify(ent => {
                        const todayEntryIndex = ent.data.findIndex(data => data.date === today);
                        if (todayEntryIndex > -1) {
                            ent.data[todayEntryIndex].count = ent.data[todayEntryIndex].count + 1;
                        } else {
                            ent.data.push({date: today, count: 1});
                        }
                    });
            } else {
                db.entitys.add({...entity, collectionDate, data: [{date: today, count: 1}]});
            }
        }
    }).catch(e => {
        console.log('error adding new entry  entitys', e);
    });
};

export const configuration = {
    ...baseConfiguration,
    minCount: 3,
    maxEntities: 5,
    neverAllKey: 'homepageNeverShowAgain'
};

export const checkEntitySuggestion = async (homePageEntites: any[], isSnoozed) => {
    let lastCollectionDate, shouldExecute;
    let blackList = [],
        entitsMonth = [];
    let neverShowAgain = await isNeverShowAgain();

    checkClearStorage();

    if (!neverShowAgain) {
        neverShowAgain = await getNeverShowAgainServer(configuration.neverAllKey);
    }

    await db
        .transaction('r', db.collectionDates, db.blackList, db.entitys, async () => {
            const collectionDate = isSnoozed
                ? await getPrevUserCollectionDate(db, getUsername())
                : await getLastUserCollectionDates(db, getUsername());

            if (!collectionDate || neverShowAgain) return;

            lastCollectionDate = moment(collectionDate?.date, 'DD/MM/YYYY');
            const daysDiff = configuredDate().diff(lastCollectionDate, 'days');

            shouldExecute = isSnoozed || daysDiff >= configuration.days;
            if (shouldExecute) {
                entitsMonth = await db.entitys
                    .where('[collectionDate+user]')
                    .equals([collectionDate.date, getUsername()])
                    .toArray();

                blackList = await db.blackList.where('user').equals(getUsername()).toArray();
            }
        })
        .catch(e => console.log('get data from indexedDB error: ', e));
    if (!neverShowAgain) {
        const entities = shouldExecute
            ? calcEntitiesToAdd(entitsMonth, blackList, homePageEntites)
            : [];

        if (entities.length > 0) {
            return entities;
        }
    }

    return [];
};

export const isNeverShowAgain = async (): Promise<boolean> => {
    return await db.transaction('r', db.collectionDates, db.blackList, db.entitys, async () => {
        const lastCollection = await db.collectionDates.toCollection().last();
        return lastCollection?.neverShowAgain;
    });
};

const calcEntitiesToAdd = (entitsMonth, blackList, homePageEntites) => {
    const monthlyUseArray = [];
    const {minCount, maxEntities} = configuration;

    // count entity uses
    entitsMonth.forEach(entity => {
        let count = 0;
        entity.data.forEach(data => (count += data.count));
        monthlyUseArray.push({
            name: entity.name,
            count,
            title: entity.title,
            exec: entity.exec,
            type: entity.type
        });
    });

    const sortedTotal = monthlyUseArray.sort((a: any, b: any) => b.count - a.count);

    const entities = [];
    sortedTotal?.forEach(item => {
        if (
            entities?.length < maxEntities &&
            item.count >= minCount &&
            !blackList.some(blItem => blItem.name === item.name && blItem.type === item.type) &&
            !isInHomepage(item, homePageEntites) &&
            !IGNORED_ENTITIES.includes(item.name)
        ) {
            entities.push(item);
        }
    });

    return entities;
};

const isInHomepage = (entity, homePageEntites): boolean => {
    let isInHomepage = false;
    homePageEntites.forEach(hpEntity => {
        let [name, type] = hpEntity.split('~');
        let entityType = entity.type;

        if (
            entityType === 'H' ||
            entityType === 'R' ||
            entityType === 'G' ||
            entityType === 'D' ||
            entityType === 'O'
        ) {
            entityType = 'P';
        }
        if (type === 'H' || type === 'R' || type === 'G' || type === 'D' || type === 'O')
            type = 'P';

        if (name === entity.name && type === entityType) {
            isInHomepage = true;
        }
    });

    return isInHomepage;
};
